import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteInfo } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 亚洲一级日本精选内容导航';

    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach((s) => {
      if (s.textContent?.includes('"@type":"Organization"')) {
        s.remove();
      }
    });

    const orgSchema = document.createElement('script');
    orgSchema.type = 'application/ld+json';
    orgSchema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteInfo.name,
      url: 'https://kaxifa.cn/',
      description: siteInfo.description,
      foundingDate: siteInfo.founded,
      contactPoint: {
        '@type': 'ContactPoint',
        email: siteInfo.email,
        contactType: 'customer service',
      },
    });
    document.head.appendChild(orgSchema);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && honeypot.toString().trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u016t3pcjqs2fcsjog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 min-h-screen">
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20Japanese%20minimalist%20interior%20with%20wooden%20elements%20and%20soft%20natural%20lighting%2C%20clean%20aesthetic%2C%20cream%20and%20earth%20tones%2C%20peaceful%20atmosphere%2C%20editorial%20photography%20style%2C%20harmonious%20composition&width=1800&height=600&seq=about-japan-hero-01&orientation=landscape"
            alt="关于我们"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-lg">
              {siteInfo.slogan}
            </p>
          </div>
        </section>

        <section className="section-padding bg-background-50">
          <div className="container-main">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                  <strong>亚洲一级日本精选内容导航</strong>成立于{siteInfo.founded}年，是专注于日本文化、影视、美食、旅游等领域的中文内容聚合平台。
                  我们致力于为中文用户提供最全面、最权威的日本相关内容导航服务，帮助每一位对日本感兴趣的读者高效地找到优质内容。
                  团队成员由资深日本文化研究者、媒体编辑和技术开发人员组成，以专业、客观、严谨的态度甄选每一条内容。
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-14">
                {[
                  { value: `${siteInfo.stats.categories}`, label: '一级类目', icon: 'ri-stack-line' },
                  { value: `${siteInfo.stats.articles}+`, label: '精选文章', icon: 'ri-article-line' },
                  { value: `${(siteInfo.stats.monthlyVisitors / 10000).toFixed(0)}万+`, label: '月访问量', icon: 'ri-user-line' },
                  { value: `${siteInfo.stats.satisfactionRate}%`, label: '用户满意度', icon: 'ri-heart-line' },
                ].map((stat, i) => (
                  <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-6 text-center">
                    <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                      <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                    </span>
                    <div className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">{stat.value}</div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                    如果您有任何问题、建议或合作意向，欢迎通过以下表单与我们取得联系。我们会在1-2个工作日内回复您。
                  </p>

                  {formStatus === 'success' ? (
                    <div className="bg-accent-50 border border-accent-200/70 rounded-lg p-6 text-center">
                      <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-checkbox-circle-line text-accent-600 text-2xl"></i>
                      </span>
                      <p className="text-sm text-foreground-800 font-medium">感谢您的留言！我们会尽快回复。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                      {formStatus === 'error' && formError && (
                        <div className="bg-red-50 border border-red-200/70 rounded-md p-3 text-sm text-red-700">
                          {formError}
                        </div>
                      )}
                      <div>
                        <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                        <input
                          id="about-subject"
                          name="subject"
                          type="text"
                          required
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                        <textarea
                          id="about-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                      </div>
                      <div className="anti-spam-field">
                        <input
                          name="phone_alt"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200"
                      >
                        发送留言
                      </button>
                    </form>
                  )}
                </div>

                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">
                    用户口碑
                  </h2>
                  <div className="space-y-4">
                    {[
                      {
                        name: '王先生',
                        role: '日本文化爱好者',
                        text: '这个网站的内容分类非常清晰，每次想了解日本的某个方面都能快速找到高质量的文章，推荐给所有对日本感兴趣的朋友！',
                      },
                      {
                        name: '李女士',
                        role: '自由行达人',
                        text: '通过亚洲一级日本的旅游攻略，我规划了一次完美的北海道之旅。信息非常实用，更新也很及时，已经成了我出行前的必查网站。',
                      },
                      {
                        name: '张同学',
                        role: '日语专业学生',
                        text: '作为日语专业的学生，这个网站帮我拓展了很多课堂外的日本文化知识。影视推荐和动漫解析都很专业，受益匪浅。',
                      },
                    ].map((review, i) => (
                      <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                        <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                          &ldquo;{review.text}&rdquo;
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-xs font-medium text-primary-700">{review.name.charAt(0)}</span>
                          </span>
                          <div>
                            <span className="text-xs font-medium text-foreground-800 block">{review.name}</span>
                            <span className="text-xs text-foreground-400">{review.role}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}